import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from 'src/app/components/shared/dropdown';
import { CategoryService } from 'src/app/services/category';
import { LocaleService } from 'src/app/services/locale';
import { TrashComponent } from '../../../../icons/trash';
import { DateRangePickerComponent } from '../../../../shared/month-picker';

interface TransportType {
  name: string;
  uuid: string;
}

interface TransportMode {
  group: string;
  name: string;
  uuid: string;
  requiresVolume?: boolean;
  requiresTeu?: boolean;
  hasRefrigeratedOption?: boolean;
  refrigeratedUuid?: string;
  italianName: string;
  subModes?: TransportMode[];
}

interface DateRange {
  start: Date | null;
  end: Date | null;
}

@Component({
  selector: 'app-category3-question2',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    DateRangePickerComponent
  ],
  template: `
    <form
      [formGroup]="form"
      class="w-full bg-white flex flex-col px-4 sm:px-5 py-2 shadow-md drop-shadow-md rounded-lg mt-4 my-2"
    >
      <!-- Applicability Questions -->
      <div class="mb-6">
        <div class="text-[14px] font-medium mb-2">
          {{ strings[language].applicability1 }}
        </div>
        <div class="flex flex-wrap gap-4 sm:gap-6">
          <div
            (click)="setApplicability1(true)"
            class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
          >
            <div
              [ngClass]="{ 'bg-[#183A25]': form.controls.q1.value === true }"
              class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
            ></div>
            <div>{{ strings[language].yes }}</div>
          </div>
          <div
            (click)="setApplicability1(false)"
            class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
          >
            <div
              [ngClass]="{
                'bg-[#183A25]': form.controls.q1.value === false
              }"
              class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
            ></div>
            <div>{{ strings[language].no }}</div>
          </div>
        </div>
      </div>
      <div *ngIf="form.value.q1 === true" class="mb-6">
        <div class="text-[14px] font-medium mb-2">
          {{ strings[language].applicability2 }}
        </div>
        <div class="flex flex-wrap gap-4 sm:gap-6">
          <div
            (click)="form.controls.q2.setValue(true)"
            class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
          >
            <div
              [ngClass]="{ 'bg-[#183A25]': form.controls.q2.value === true }"
              class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
            ></div>
            <div>{{ strings[language].yes }}</div>
          </div>
          <div
            (click)="form.controls.q2.setValue(false)"
            class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
          >
            <div
              [ngClass]="{
                'bg-[#183A25]': form.controls.q2.value === false
              }"
              class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
            ></div>
            <div>{{ strings[language].no }}</div>
          </div>
        </div>
      </div>
      <!-- Method Selection -->
      <div
        *ngIf="form.value.q1 === true && form.value.q2 === true"
        class="mb-6"
      >
        <div class="text-[14px] font-medium mb-2">
          {{ strings[language].methodSelection }}
        </div>
        <div class="flex  gap-5">
          <div
            (click)="form.controls.method.setValue('spend')"
            class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
          >
            <div
              [ngClass]="{
                'bg-[#183A25]': form.controls.method.value === 'spend'
              }"
              class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
            ></div>
            <div>{{ strings[language].spendMethod }}</div>
            <div class="relative group ml-2 mt-2">
              <button
                type="button"
                class="text-gray-500 hover:text-gray-700 focus:outline-none"
                (click)="$event.stopPropagation()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
              <div
                class="absolute z-10 hidden group-hover:block w-64 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
              >
                {{ strings[language].spendHelp }}
              </div>
            </div>
          </div>

          <div
            (click)="form.controls.method.setValue('activity')"
            class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
          >
            <div
              [ngClass]="{
                'bg-[#183A25]': form.controls.method.value === 'activity'
              }"
              class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
            ></div>
            <div>{{ strings[language].activityMethod }}</div>
            <div class="relative group ml-2 mt-2">
              <button
                type="button"
                class="text-gray-500 hover:text-gray-700 focus:outline-none"
                (click)="$event.stopPropagation()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
              <div
                class="absolute z-10 hidden group-hover:block w-64 bg-white p-2 border border-gray-200 rounded shadow-lg text-sm left-0 bottom-full mb-2"
              >
                {{ strings[language].activityHelp }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Spend Based Form -->
      <div
        *ngIf="
          form.value.q1 === true &&
          form.value.q2 === true &&
          form.value.method === 'spend'
        "
        class="mb-6"
      >
        <div>
          <table class="w-full transformSize ">
            <thead>
              <tr>
                <th class="px-4 py-2 text-left">
                  {{ strings[language].transportType }}
                </th>
                <th class="px-4 py-2 text-left">
                  {{ strings[language].freightCost }}
                </th>
                <th class="px-4 py-2 text-left">
                  {{ strings[language].currency }}
                </th>
                <th class="px-4 py-2 text-left w-1/3">
                  <!-- Wider column -->
                  {{ strings[language].period }}
                </th>
              </tr>
            </thead>
            <tbody formArrayName="spendData">
              <tr
                *ngFor="let row of spendRows.controls; let i = index"
                [formGroupName]="i"
                class="border-t"
              >
                <td class="px-4 py-2">
                  <div class="font-medium">{{ transportTypes[i].name }}</div>
                  <input
                    type="hidden"
                    [formControl]="getFormControl(row, 'uuid')"
                  />
                </td>
                <td class="px-4 py-2">
                  <app-input
                    type="number"
                    [control]="getFormControl(row, 'freightCost')"
                    [placeholder]="strings[language].enterCost"
                    [keyUp]="onChange"
                  ></app-input>
                </td>
                <td class="px-4 py-2">
                  <app-dropdown
                    [placeholder]="strings[language].selectCurrency"
                    [options]="currencyOptions"
                    [control]="getFormControl(row, 'currency')"
                  ></app-dropdown>
                </td>
                <td class="px-4 py-2 ">
                  <!-- Try w-full or max-w-* -->
                  <app-month-range-picker
                    [control]="getFormControl(row, 'period')"
                    (rangeChange)="onPeriodChange(row, $event)"
                  ></app-month-range-picker>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="mt-2 text-sm text-gray-600 italic">
            {{ strings[language].spendHelpTable }}
          </div>
        </div>
      </div>
      <!-- Activity Based Form -->
      <div
        *ngIf="
          form.value.q1 === true &&
          form.value.q2 === true &&
          form.value.method === 'activity'
        "
        class="mb-6"
      >
        <button
          type="button"
          style="background-color: #043B22;"
          class="text-white px-4 py-2 rounded mb-4"
          (click)="addShipment()"
        >
          + {{ strings[language].addShipment }}
        </button>
        <div formArrayName="activityData" class="space-y-4">
          <div
            *ngFor="let shipment of activityShipments.controls; let i = index"
            [formGroupName]="i"
            class="border rounded-lg p-4"
          >
            <div
              class="flex justify-between items-center mb-4 bg-[#F8F9FA] p-3 rounded text-lg"
            >
              <div class="font-medium">
                {{ strings[language].shipment }} #{{ i + 1 }}
              </div>
              <div
                (click)="removeShipment(i)"
                class="flex justify-start  align-middle items-center cursor-pointer "
              >
                <app-trash></app-trash>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Reference -->
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].reference
                }}</label>
                <app-input
                  type="text"
                  [control]="getFormControl(shipment, 'reference')"
                  [placeholder]="strings[language].enterReference"
                  [keyUp]="onChange"
                ></app-input>
              </div>
              <!-- Date -->
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].date
                }}</label>
                <app-input
                  type="month"
                  [control]="getFormControl(shipment, 'date')"
                  [keyUp]="onChange"
                ></app-input>
              </div>

              <!-- Transport Mode Group -->
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].transportModeGroup
                }}</label>
                <app-dropdown
                  [placeholder]="strings[language].selectTransportModeGroup"
                  [options]="transportModeGroupOptions"
                  [control]="getFormControl(shipment, 'transportModeGroup')"
                  (change)="onModeGroupChange(shipment)"
                ></app-dropdown>
              </div>
              <!-- Transport Mode -->
              <div>
                <div
                  class="flex items-center justify-between gap-4 flex-wrap md:flex-nowrap mb-1"
                >
                  <div class="text-sm font-medium whitespace-nowrap">
                    {{ strings[language].transportMode }}
                  </div>
                  <ng-container *ngIf="showRefrigeratedOptionsToggle(shipment)">
                    <div class="flex items-center gap-1 whitespace-nowrap">
                      <label
                        class="relative inline-flex items-center cursor-pointer"
                        style="transform: scale(0.85);"
                      >
                        <input
                          type="checkbox"
                          class="sr-only peer"
                          [formControl]="
                            getFormControl(shipment, 'showRefrigerated')
                          "
                          (change)="onRefrigeratedToggleChange(shipment)"
                        />
                        <div
                          class="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full
            peer peer-checked:after:translate-x-full peer-checked:after:border-white
            after:content-[''] after:absolute after:top-[2px] after:left-[2px]
            after:bg-white after:border-gray-300 after:border after:rounded-full
            after:h-4 after:w-4 after:transition-all peer-checked:bg-green-800"
                        ></div>
                      </label>
                      <span class="text-sm">
                        {{ strings[language].refrigerated }}
                      </span>
                    </div>
                  </ng-container>
                </div>
                <app-dropdown
                  [placeholder]="strings[language].selectTransportMode"
                  [options]="
                    getTransportModeOptions(
                      getFormControl(shipment, 'transportModeGroup').value,
                      getFormControl(shipment, 'showRefrigerated').value
                    )
                  "
                  [control]="getFormControl(shipment, 'transportMode')"
                  [trackBy]="trackByOption"
                  (change)="onModeChange(shipment)"
                ></app-dropdown>
              </div>
              <!-- Refrigerated Toggle - Hidden but functional -->
              <div
                *ngIf="showRefrigeratedToggle(shipment)"
                style="display: none;"
              >
                <div class="flex gap-4">
                  <div
                    (click)="
                      getFormControl(shipment, 'isRefrigerated').setValue(true)
                    "
                    class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
                  >
                    <div
                      [ngClass]="{
                        'bg-[#183A25]':
                          getFormControl(shipment, 'isRefrigerated').value ===
                          true
                      }"
                      class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
                    ></div>
                    <div>{{ strings[language].yes }}</div>
                  </div>
                  <div
                    (click)="
                      getFormControl(shipment, 'isRefrigerated').setValue(false)
                    "
                    class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
                  >
                    <div
                      [ngClass]="{
                        'bg-[#183A25]':
                          getFormControl(shipment, 'isRefrigerated').value ===
                          false
                      }"
                      class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
                    ></div>
                    <div>{{ strings[language].no }}</div>
                  </div>
                </div>
              </div>
              <!-- Weight -->
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ strings[language].weight }} (kg)</label
                >
                <app-input
                  type="number"
                  [control]="getFormControl(shipment, 'weight')"
                  [placeholder]="strings[language].enterWeight"
                  [keyUp]="onChange"
                ></app-input>
              </div>
              <!-- Distance -->
              <div>
                <label class="block text-sm font-medium mb-1"
                  >{{ strings[language].distance }} (km)</label
                >
                <app-input
                  type="number"
                  [control]="getFormControl(shipment, 'distance')"
                  [placeholder]="strings[language].enterDistance"
                  [keyUp]="onChange"
                ></app-input>
              </div>
              <!-- Volume -->
              <div *ngIf="showVolumeField(shipment)">
                <label class="block text-sm font-medium mb-1"
                  >{{ strings[language].volume }} (m³)</label
                >
                <app-input
                  type="number"
                  [control]="getFormControl(shipment, 'volume')"
                  [placeholder]="strings[language].enterVolume"
                  [keyUp]="onChange"
                ></app-input>
              </div>
              <!-- TEU -->
              <div *ngIf="showTeuField(shipment)">
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].teu
                }}</label>
                <app-input
                  type="number"
                  [control]="getFormControl(shipment, 'teu')"
                  [placeholder]="strings[language].enterTeu"
                  [keyUp]="onChange"
                ></app-input>
              </div>
              <!-- Commodity -->
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ strings[language].commodity }}
                </label>
                <app-dropdown
                  [placeholder]="strings[language].selectCommodity"
                  [options]="commodityOptions[language]"
                  [control]="getFormControl(shipment, 'commodity')"
                  (change)="onCommodityChange(shipment)"
                ></app-dropdown>
              </div>
              <!-- Temperature Controlled -->
              <div>
                <label class="block text-sm font-medium mb-1">
                  {{ strings[language].temperatureControlled }}
                </label>
                <div class="flex gap-4">
                  <div
                    (click)="
                      getFormControl(
                        shipment,
                        'temperatureControlled'
                      ).setValue(true)
                    "
                    class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
                  >
                    <div
                      [ngClass]="{
                        'bg-[#183A25]':
                          getFormControl(shipment, 'temperatureControlled')
                            .value === true
                      }"
                      class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
                    ></div>
                    <div>{{ strings[language].yes }}</div>
                  </div>
                  <div
                    (click)="
                      getFormControl(
                        shipment,
                        'temperatureControlled'
                      ).setValue(false)
                    "
                    class="flex gap-2 align-middle items-center cursor-pointer text-[13px]"
                  >
                    <div
                      [ngClass]="{
                        'bg-[#183A25]':
                          getFormControl(shipment, 'temperatureControlled')
                            .value === false
                      }"
                      class="w-4 h-4 border border-gray-500 rounded-full flex-shrink-0"
                    ></div>
                    <div>{{ strings[language].no }}</div>
                  </div>
                </div>
              </div>
              <div>
                <div *ngIf="showOtherCommodityInput(shipment)">
                  <label class="block text-sm font-medium mb-1">
                    {{ strings[language].specifyCommodity }}
                  </label>
                  <app-input
                    type="text"
                    [control]="getFormControl(shipment, 'otherCommodity')"
                    [placeholder]="strings[language].enterCommodity"
                    [keyUp]="onChange"
                  ></app-input>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  `,
  styles: [
    `
      table {
        width: 100%;
        border-collapse: collapse;
      }
      td,
      th {
        padding: 0.75rem;
        border: 1px solid #e5e7eb;
        text-align: left;
        white-space: normal; /* Allow text to wrap */
        word-wrap: break-word;
      }

      th {
        background-color: #f9fafb;
        font-weight: 500;
      }
    `
  ]
})
export class Category3Question2Component implements OnInit {
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  categories = this.categoryService.categories();
  fb = inject(FormBuilder);

  form = this.fb.group({
    q1: [false, Validators.required],
    q2: [false],
    method: [null as 'spend' | 'activity' | null],
    spendData: this.fb.array<FormGroup>([]),
    activityData: this.fb.array<FormGroup>([])
  });

  transportTypes: TransportType[] = [
    {
      name: this.language === 'english' ? 'Sea' : 'Trasporto marittimo',
      uuid: '6e7dabe4-8682-45cc-8980-0ce6dfd9c148'
    },
    {
      name: this.language === 'english' ? 'Air' : 'Trasporto aereo',
      uuid: 'dec18110-bd4c-4913-b1a3-687819f24dba'
    },
    {
      name: this.language === 'english' ? 'Rail' : 'Trasporto ferroviario',
      uuid: 'eaf1a36a-2106-4c97-9eec-ad1177330fff'
    },
    {
      name: this.language === 'english' ? 'Pipelines' : 'Oleodotti / Gasdotti',
      uuid: '55681a50-7d9b-4ffb-88df-5aba65d7c744'
    },
    {
      name:
        this.language === 'english'
          ? 'Inland'
          : 'Trasporto terrestre (su strada)',
      uuid: 'fdc1d7a7-1352-4747-8032-f03d4a9f8665'
    },
    {
      name: this.language === 'english' ? 'Water inland' : 'Trasporto fluviale',
      uuid: '7af61449-1035-4662-ad47-027e8d9d169a'
    }
  ];

  transportModes: TransportMode[] = [
    {
      group: 'Road',
      name: 'Road',
      uuid: 'road-group',
      italianName: 'Strada',
      subModes: [
        {
          group: 'Road',
          name: 'Van ≤ 3.5t',
          italianName: 'Furgone ≤ 3,5t',
          uuid:
            'freight_vehicle-vehicle_type_van-fuel_source_na-vehicle_weight_lte_3.5t-distance_uplift_included',
          hasRefrigeratedOption: true,
          refrigeratedUuid:
            'freight_vehicle-vehicle_type_van_refrigerated-fuel_source_na-vehicle_weight_lte_3.5t-distance_uplift_included'
        },
        {
          group: 'Road',
          name: 'Rigid Truck 3.5t - 7.5t (Diesel)',
          italianName: 'Camion rigido 3,5t - 7,5t (Diesel)',
          uuid:
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_3.5t_lte_7.5t-distance_basis_sfd',
          hasRefrigeratedOption: true,
          refrigeratedUuid:
            'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_3.5t_lt_7.5t-distance_uplift_included'
        },
        {
          group: 'Road',
          name: 'Rigid Truck 7.5t - 12t (Diesel)',
          italianName: 'Camion rigido 7,5t - 12t (Diesel)',
          uuid:
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_7.5t_lte_12t-distance_basis_sfd',
          hasRefrigeratedOption: true,
          refrigeratedUuid:
            'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_7.5t_lt_12t-distance_uplift_included'
        },
        {
          group: 'Road',
          name: 'Rigid truck (12 t – 20 t) Diesel',
          italianName: 'Camion rigido (12t – 20t) Diesel',
          uuid:
            'freight_vehicle-vehicle_type_rigid_truck-fuel_source_diesel-vehicle_weight_gt_12t_lte_20t-distance_basis_sfd',
          hasRefrigeratedOption: true,
          refrigeratedUuid:
            'freight_vehicle-vehicle_type_hgv_rigid_refrigerated-fuel_source_diesel_5_percent_biodiesel_blend-vehicle_weight_gt_12t_lt_20t-distance_uplift_included'
        },
        {
          group: 'Road',
          name: 'HGV >20',
          italianName: 'Autocarro pesante >20t',
          uuid:
            'freight_vehicle-vehicle_type_hgv-fuel_source_na-vehicle_weight_gt_20t-distance_uplift_included',
          hasRefrigeratedOption: true,
          refrigeratedUuid:
            'freight_vehicle-vehicle_type_hgv_refrigerated-fuel_source_na-vehicle_weight_gt_20t-distance_uplift_included'
        }
      ]
    },
    {
      group: 'Rail',
      name: 'Rail',
      uuid: 'rail-group',
      italianName: 'Treno',
      subModes: [
        {
          group: 'Rail',
          name: 'Average traction mix',
          italianName: 'Mix medio di trazione',
          uuid: 'freight_train-route_type_na-fuel_type_na'
        },
        {
          group: 'Rail',
          name: 'Diesel traction',
          italianName: 'Trazione diesel',
          uuid: 'freight_train-route_type_na-fuel_type_diesel'
        },
        {
          group: 'Rail',
          name: 'Electric traction',
          italianName: 'Trazione elettrica',
          uuid: 'freight_train-route_type_na-fuel_type_electricity'
        }
      ]
    },
    {
      group: 'Sea',
      name: 'Sea',
      uuid: 'sea-group',
      italianName: 'Mare',
      subModes: [
        {
          group: 'Sea',
          name: 'Container ship (dry, FCL)',
          italianName: 'Nave container (secco, FCL)',
          uuid: 'sea_freight-vessel_type_container-distance_basis_sfd',
          requiresTeu: true
        },
        {
          group: 'Sea',
          name: 'Container ship (dry, LCL)',
          italianName: 'Nave container (secco, LCL)',
          uuid: 'sea_freight-vessel_type_container-distance_basis_sfd',
          requiresVolume: true
        },
        {
          group: 'Sea',
          name: 'Container ship (reefer)',
          italianName: 'Nave container refrigerata (reefer)',
          uuid:
            'sea_freight-vessel_type_container_reefer_refrigerated-distance_basis_sfd'
        },
        {
          group: 'Sea',
          name: 'Ro-ro vessel (mixed cargo)',
          italianName: 'Nave Ro-Ro (carico misto)',
          uuid:
            'sea_freight-vessel_type_ro_ro-route_type_na-vessel_length_na-tonnage_gte_10dwkt_lt_15dwkt-fuel_source_hfo-load_type_na-distance_basis_sfd'
        },
        {
          group: 'Sea',
          name: 'Bulk carrier (Other)',
          italianName: 'Nave bulk carrier (Altro)',
          uuid:
            'sea_freight-vessel_type_bulk_carrier-route_type_na-vessel_length_na-tonnage_na-fuel_source_na'
        },
        {
          group: 'Sea',
          name: 'Tanker (≤ 9 999 dwt)',
          italianName: 'Petroliera (≤ 9 999 dwt)',
          uuid:
            'sea_freight-vessel_type_tanker-route_type_na-vessel_length_na-tonnage_gt_0_lt_9999dwt-fuel_source_na'
        },
        {
          group: 'Sea',
          name: 'Tanker (100 000–199 999 dwt)',
          italianName: 'Petroliera (100 000–199 999 dwt)',
          uuid:
            'sea_freight-vessel_type_tanker-route_type_na-vessel_length_na-tonnage_gt_100000dwt_lt_199999dwt-fuel_source_na'
        },
        {
          group: 'Sea',
          name: 'Tanker (≥ 200 000 dwt)',
          italianName: 'Petroliera (≥ 200 000 dwt)',
          uuid:
            'sea_freight-vessel_type_tanker-route_type_na-vessel_length_na-tonnage_gt_200000dwt-fuel_source_na'
        }
      ]
    },
    {
      group: 'Air',
      name: 'Air',
      uuid: 'air-group',
      italianName: 'Aereo',
      subModes: [
        {
          group: 'Air',
          name: 'Unknown mix (≤ 1 500 km)',
          italianName: 'Mix sconosciuto (≤ 1 500 km)',
          uuid:
            'freight_flight-route_type_na-distance_lte_1500km-weight_na-rf_excluded-aircraft_type_unknown-distance_basis_gcd'
        },
        {
          group: 'Air',
          name: 'Unknown mix (> 1 500 km)',
          italianName: 'Mix sconosciuto (> 1 500 km)',
          uuid:
            'freight_flight-route_type_na-distance_gt_1500km-weight_na-rf_excluded-aircraft_type_unknown-distance_basis_gcd'
        },
        {
          group: 'Air',
          name: 'Belly-hold (≤ 1 500 km)',
          italianName: 'Stiva aerea (≤ 1 500 km)',
          uuid:
            'freight_flight-route_type_na-distance_lte_1500km-weight_na-rf_excluded-aircraft_type_belly_freight-distance_basis_gcd'
        },
        {
          group: 'Air',
          name: 'Belly-hold (> 1 500 km)',
          italianName: 'Stiva aerea (> 1 500 km)',
          uuid:
            'freight_flight-route_type_na-distance_gt_1500km-weight_na-rf_excluded-aircraft_type_belly_freight-distance_basis_gcd'
        },
        {
          group: 'Air',
          name: 'Freighter (≤ 1 500 km)',
          italianName: 'Cargo (≤ 1 500 km)',
          uuid:
            'freight_flight-route_type_na-distance_lte_1500km-weight_na-rf_excluded-aircraft_type_freighter-distance_basis_gcd'
        },
        {
          group: 'Air',
          name: 'Freighter (> 1 500 km)',
          italianName: 'Cargo (> 1 500 km)',
          uuid:
            'freight_flight-route_type_na-distance_gt_1500km-weight_na-rf_excluded-aircraft_type_freighter-distance_basis_gcd'
        }
      ]
    }
  ];

  currencyOptions = [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' }
  ];

  commodityOptions: any = {
    english: [
      {
        value: '01',
        label: 'Live animals & aquaculture',
        italianLabel: 'Animali vivi e acquacoltura'
      },
      {
        value: '02',
        label: 'Grains, pulses, oil-seeds & other dry agri-bulk',
        italianLabel:
          'Cereali, legumi, semi oleosi e altre materie prime agricole secche'
      },
      {
        value: '03',
        label: 'Fresh fruit & vegetables (chilled)',
        italianLabel: 'Frutta e verdura fresca (refrigerata)'
      },
      {
        value: '04',
        label: 'Temperature-controlled foods (meat, seafood, dairy, frozen)',
        italianLabel:
          'Alimenti a temperatura controllata (carne, pesce, latticini, surgelati)'
      },
      {
        value: '05',
        label: 'Sugar, cocoa, coffee, tea & other dry food ingredients',
        italianLabel:
          'Zucchero, cacao, caffè, tè e altri ingredienti alimentari secchi'
      },
      {
        value: '06',
        label: 'Beverages (water, soft drinks, beer, wine, spirits)',
        italianLabel: 'Bevande (acqua, bibite, birra, vino, alcolici)'
      },
      {
        value: '07',
        label: 'Wood, timber, pulp, paper & printed products',
        italianLabel:
          'Legno, legname, pasta di legno, carta e prodotti stampati'
      },
      {
        value: '08',
        label: 'Textiles, leather, footwear & fashion accessories',
        italianLabel: 'Tessili, pelle, calzature e accessori moda'
      },
      {
        value: '09',
        label: 'Plastics, rubber & other polymer products (incl. packaging)',
        italianLabel:
          'Materie plastiche, gomma e altri prodotti polimerici (compresi gli imballaggi)'
      },
      {
        value: '10',
        label:
          'Basic chemicals, fertilisers, resins – non-hazardous powders / granules',
        italianLabel:
          'Prodotti chimici di base, fertilizzanti, resine - polveri/granuli non pericolosi'
      },
      {
        value: '11',
        label: 'Hazardous chemicals & dangerous goods (ADR / IMO classes)',
        italianLabel:
          'Sostanze chimiche pericolose e merci pericolose (classi ADR/IMO)'
      },
      {
        value: '12',
        label: 'Pharmaceuticals, cosmetics & medical / life-science supplies',
        italianLabel: 'Farmaci, cosmetici e forniture mediche/scientifiche'
      },
      {
        value: '13',
        label: 'Mineral ores & concentrates (iron, bauxite, copper, etc.)',
        italianLabel: 'Minerali e concentrati (ferro, bauxite, rame, ecc.)'
      },
      {
        value: '14',
        label: 'Energy solids – coal, coke, lignite, biomass pellets',
        italianLabel:
          'Solidi energetici - carbone, coke, lignite, pellet di biomassa'
      },
      {
        value: '15',
        label:
          'Liquid bulk – crude oil, refined petroleum, edible oils, chemicals',
        italianLabel:
          'Liquidi sfusi - petrolio greggio, petrolio raffinato, oli commestibili, prodotti chimici'
      },
      {
        value: '16',
        label:
          'Construction materials (cement, lime, glass, bricks, tiles, gypsum)',
        italianLabel:
          'Materiali da costruzione (cemento, calce, vetro, mattoni, piastrelle, gesso)'
      },
      {
        value: '17',
        label: 'Semi-finished metals (slabs, coils, bars, billets, pipes)',
        italianLabel:
          'Semilavorati metallici (lamiere, bobine, barre, billetti, tubi)'
      },
      {
        value: '18',
        label: 'Machinery & industrial equipment (capital goods)',
        italianLabel: 'Macchinari e attrezzature industriali (beni strumentali)'
      },
      {
        value: '19',
        label:
          'Electrical & electronic equipment (IT, telecom, appliances, AV)',
        italianLabel:
          'Apparecchiature elettriche ed elettroniche (IT, telecomunicazioni, elettrodomestici, AV)'
      },
      {
        value: '20',
        label: 'Automotive parts, tyres & finished vehicles',
        italianLabel: 'Parti automobilistiche, pneumatici e veicoli finiti'
      },
      {
        value: '21',
        label: 'Furniture & household / office furnishings',
        italianLabel: 'Mobili e arredamento per la casa/ufficio'
      },
      {
        value: '22',
        label: 'Consumer packaged goods & mixed retail cargo',
        italianLabel:
          'Prodotti confezionati per il consumo e carichi misti al dettaglio'
      },
      {
        value: '23',
        label: 'Express parcels & e-commerce small packages',
        italianLabel: 'Pacchi espressi e piccoli colli e-commerce'
      },
      {
        value: '24',
        label: 'Empty packaging, pallets, returnables, dunnage',
        italianLabel:
          'Imballaggi vuoti, pallet, materiali riutilizzabili, materiali di riempimento'
      },
      { value: '25', label: 'Other', italianLabel: 'Altro' }
    ],
    italian: [
      {
        value: '01',
        label: 'Animali vivi e acquacoltura',
        englishLabel: 'Live animals & aquaculture'
      },
      {
        value: '02',
        label:
          'Cereali, legumi, semi oleosi e altre materie prime agricole secche',
        englishLabel: 'Grains, pulses, oil-seeds & other dry agri-bulk'
      },
      {
        value: '03',
        label: 'Frutta e verdura fresca (refrigerata)',
        englishLabel: 'Fresh fruit & vegetables (chilled)'
      },
      {
        value: '04',
        label:
          'Alimenti a temperatura controllata (carne, pesce, latticini, surgelati)',
        englishLabel:
          'Temperature-controlled foods (meat, seafood, dairy, frozen)'
      },
      {
        value: '05',
        label:
          'Zucchero, cacao, caffè, tè e altri ingredienti alimentari secchi',
        englishLabel: 'Sugar, cocoa, coffee, tea & other dry food ingredients'
      },
      {
        value: '06',
        label: 'Bevande (acqua, bibite, birra, vino, alcolici)',
        englishLabel: 'Beverages (water, soft drinks, beer, wine, spirits)'
      },
      {
        value: '07',
        label: 'Legno, legname, pasta di legno, carta e prodotti stampati',
        englishLabel: 'Wood, timber, pulp, paper & printed products'
      },
      {
        value: '08',
        label: 'Tessili, pelle, calzature e accessori moda',
        englishLabel: 'Textiles, leather, footwear & fashion accessories'
      },
      {
        value: '09',
        label:
          'Materie plastiche, gomma e altri prodotti polimerici (compresi gli imballaggi)',
        englishLabel:
          'Plastics, rubber & other polymer products (incl. packaging)'
      },
      {
        value: '10',
        label:
          'Prodotti chimici di base, fertilizzanti, resine - polveri/granuli non pericolosi',
        englishLabel:
          'Basic chemicals, fertilisers, resins – non-hazardous powders / granules'
      },
      {
        value: '11',
        label:
          'Sostanze chimiche pericolose e merci pericolose (classi ADR/IMO)',
        englishLabel:
          'Hazardous chemicals & dangerous goods (ADR / IMO classes)'
      },
      {
        value: '12',
        label: 'Farmaci, cosmetici e forniture mediche/scientifiche',
        englishLabel:
          'Pharmaceuticals, cosmetics & medical / life-science supplies'
      },
      {
        value: '13',
        label: 'Minerali e concentrati (ferro, bauxite, rame, ecc.)',
        englishLabel:
          'Mineral ores & concentrates (iron, bauxite, copper, etc.)'
      },
      {
        value: '14',
        label: 'Solidi energetici - carbone, coke, lignite, pellet di biomassa',
        englishLabel: 'Energy solids – coal, coke, lignite, biomass pellets'
      },
      {
        value: '15',
        label:
          'Liquidi sfusi - petrolio greggio, petrolio raffinato, oli commestibili, prodotti chimici',
        englishLabel:
          'Liquid bulk – crude oil, refined petroleum, edible oils, chemicals'
      },
      {
        value: '16',
        label:
          'Materiali da costruzione (cemento, calce, vetro, mattoni, piastrelle, gesso)',
        englishLabel:
          'Construction materials (cement, lime, glass, bricks, tiles, gypsum)'
      },
      {
        value: '17',
        label:
          'Semilavorati metallici (lamiere, bobine, barre, billetti, tubi)',
        englishLabel:
          'Semi-finished metals (slabs, coils, bars, billets, pipes)'
      },
      {
        value: '18',
        label: 'Macchinari e attrezzature industriali (beni strumentali)',
        englishLabel: 'Machinery & industrial equipment (capital goods)'
      },
      {
        value: '19',
        label:
          'Apparecchiature elettriche ed elettroniche (IT, telecomunicazioni, elettrodomestici, AV)',
        englishLabel:
          'Electrical & electronic equipment (IT, telecom, appliances, AV)'
      },
      {
        value: '20',
        label: 'Parti automobilistiche, pneumatici e veicoli finiti',
        englishLabel: 'Automotive parts, tyres & finished vehicles'
      },
      {
        value: '21',
        label: 'Mobili e arredamento per la casa/ufficio',
        englishLabel: 'Furniture & household / office furnishings'
      },
      {
        value: '22',
        label:
          'Prodotti confezionati per il consumo e carichi misti al dettaglio',
        englishLabel: 'Consumer packaged goods & mixed retail cargo'
      },
      {
        value: '23',
        label: 'Pacchi espressi e piccoli colli e-commerce',
        englishLabel: 'Express parcels & e-commerce small packages'
      },
      {
        value: '24',
        label:
          'Imballaggi vuoti, pallet, materiali riutilizzabili, materiali di riempimento',
        englishLabel: 'Empty packaging, pallets, returnables, dunnage'
      },
      { value: '25', label: 'Altro', englishLabel: 'Other' }
    ]
  };

  strings: any = {
    english: {
      temperatureControlled: 'Temperature Controlled',
      applicability1:
        'Have any finished goods been sold or dispatched during the reporting year?',
      applicability2:
        'Are any of these goods transported, distributed or stored in vehicles, vessels, pipelines or warehouses that your organisation does not own, lease or directly fuel?',
      methodSelection:
        'Select which method is more convenient based on your data:',
      spendHelpTable:
        'Only for inbound transport. Exclude VAT, surcharges, and customs duties.',
      spendMethod: 'Spend based method',
      activityMethod: 'Activity based method',
      transportType: 'Transport Type',
      freightCost: 'Freight Cost',
      currency: 'Currency',
      period: 'Period',
      enterCost: 'Enter cost',
      spendHelp:
        'Only outbound transport & distribution. Exclude VAT, surcharges and customs duties unless included in the freight invoice.',
      addShipment: 'Add Shipment',
      remove: 'Remove',
      shipment: 'Shipment',
      reference: 'Shipment ID / PO / Invoice reference',
      enterReference: 'Enter reference',
      date: 'Date (YYYY-MM)',
      transportModeGroup: 'Transport Mode',
      transportMode: 'Transport Options',
      selectTransportModeGroup: 'Select Transport Mode',
      selectTransportMode: 'Select Transport Options',
      weight: 'Weight of Cargo',
      enterWeight: 'Enter weight',
      distance: 'Distance Travelled',
      enterDistance: 'Enter distance',
      volume: 'Volume',
      enterVolume: 'Enter volume',
      teu: 'TEU',
      enterTeu: 'Enter TEU',
      commodity: 'Commodity',
      specifyCommodity: 'Please specify commodity',
      enterCommodity: 'Enter commodity description',
      activityHelp:
        'Only outbound flows of goods, materials or packaging that travel in vehicles, vessels, pipelines or warehouses your organisation does not own, lease or directly fuel. If you aggregate multiple shipments into one line, make sure the weight, distance and month are all averages or totals that fairly represent that group',
      yes: 'Yes',
      no: 'No',
      selectCurrency: 'Select Currency',
      selectCommodity: 'Select Commodity',
      refrigerated: 'Refrigerated'
    },
    italian: {
      temperatureControlled: 'Controllo della temperatura',
      applicability1:
        "Durante l'anno di rendicontazione sono stati venduti o spediti beni finiti?",
      applicability2:
        'Alcuni di questi beni vengono trasportati, distribuiti o immagazzinati in veicoli, navi, oleodotti o magazzini che la vostra organizzazione non possiede, non prende in leasing o non rifornisce direttamente?',
      methodSelection:
        'Seleziona il metodo più adatto in base ai dati disponibili:',
      spendHelpTable:
        'Solo per il trasporto in entrata. Escludere IVA, supplementi e dazi doganali.',
      spendMethod: 'Metodo basato sulla spesa',
      activityMethod: "Metodo basato sull'attività",
      transportType: 'Tipo di trasporto',
      freightCost: 'Costo del trasporto',
      currency: 'Valuta',
      period: 'Periodo',
      enterCost: 'Inserisci il costo',
      spendHelp:
        'Usa questo metodo se conosci solo quanto la tua organizzazione ha pagato per il trasporto, ma non hai dati su peso e distanza.',
      addShipment: 'Aggiungi spedizione',
      remove: 'Rimuovi',
      shipment: 'Spedizione',
      reference: 'ID spedizione / PO / riferimento fattura',
      enterReference: 'Inserisci riferimento',
      date: 'Data (AAAA-MM)',
      transportModeGroup: 'Modalità di trasporto',
      transportMode: 'Opzioni di trasporto',
      selectTransportModeGroup: 'Seleziona modalità di trasporto',
      selectTransportMode: 'Seleziona opzioni di trasporto',
      weight: 'Peso del carico',
      enterWeight: 'Inserisci il peso',
      distance: 'Distanza percorsa',
      enterDistance: 'Inserisci la distanza',
      volume: 'Volume',
      enterVolume: 'Inserisci il volume',
      teu: 'TEU',
      enterTeu: 'Inserisci TEU',
      commodity: 'Merce',
      specifyCommodity: 'Specifica la merce',
      enterCommodity: 'Inserisci la descrizione della merce',
      activityHelp:
        'Usa questo metodo se hai dati fisici sulla spedizione — ad esempio, tonnellate per chilometro, oppure peso/volume insieme alla distanza media — per ciascuna modalità di trasporto.',
      yes: 'Sì',
      no: 'No',
      selectCurrency: 'Seleziona la valuta',
      selectCommodity: 'Seleziona la merce',
      refrigerated: 'Refrigerato'
    }
  };

  get spendRows(): FormArray {
    return this.form.get('spendData') as FormArray;
  }

  get activityShipments(): FormArray {
    return this.form.get('activityData') as FormArray;
  }

  private _transportModeCache: { lang: string; opts: { value: string; label: string }[] } | null = null;

  get transportModeGroupOptions() {
    const lang = this.language;
    if (this._transportModeCache?.lang === lang) {
      return this._transportModeCache.opts;
    }
    const opts = this.transportModes.map(mode => ({
      value: mode.uuid,
      label: lang === 'english' ? mode.name : mode.italianName
    }));
    this._transportModeCache = { lang, opts };
    return opts;
  }

  ngOnInit() {
    this.initializeForm();
    this.form.valueChanges.subscribe(() => this.onChange());
  }

  getFormControl(group: AbstractControl, controlName: string): FormControl {
    return (group as FormGroup).get(controlName) as FormControl;
  }

  initializeForm() {
    const savedData = this.getSavedData();

    if (savedData) {
      this.form.patchValue({
        q1: savedData.q1 !== undefined ? savedData.q1 : false,
        q2: savedData.q2 !== undefined ? savedData.q2 : false,
        method: savedData.method
      });

      if (savedData.spendData) {
        this.spendRows.clear();
        savedData.spendData.forEach((item: any) => {
          this.addSpendRow({
            uuid: item.uuid,
            transportType: item.transportType,
            freightCost: item.freightCost,
            currency: item.currency || 'EUR',
            period: item.period
          });
        });
      } else {
        this.transportTypes.forEach(type =>
          this.addSpendRow({
            uuid: type.uuid,
            transportType: type.name,
            period: null
          })
        );
      }

      if (savedData.activityData) {
        this.activityShipments.clear();
        savedData.activityData.forEach((item: any) => {
          this.addShipment(item);
        });
      }
    } else {
      this.form.patchValue({
        q1: false,
        q2: false
      });
      this.transportTypes.forEach(type =>
        this.addSpendRow({
          uuid: type.uuid,
          transportType: type.name,
          period: null
        })
      );
    }
  }

  setApplicability1(value: boolean) {
    this.form.controls.q1.setValue(value);
    if (value === false) {
      // Reset the entire form
      this.form.reset({
        q1: false,
        q2: false,
        method: null
      });

      // Clear the form arrays
      this.spendRows.clear();
      this.activityShipments.clear();

      // Reinitialize the spend rows with default values
      this.transportTypes.forEach(type =>
        this.addSpendRow({
          uuid: type.uuid,
          transportType: type.name,
          period: null
        })
      );

      // Trigger change detection
      this.onChange();
    }
  }

  addSpendRow(data: any = null) {
    const typeIndex = data
      ? this.transportTypes.findIndex(t => t.uuid === data.uuid)
      : this.spendRows.length;
    const transportType =
      typeIndex >= 0 ? this.transportTypes[typeIndex] : this.transportTypes[0];
    const row = this.fb.group({
      uuid: [transportType.uuid, Validators.required],
      transportType: [transportType.name],
      freightCost: [
        data?.freightCost || null,
        [Validators.required, Validators.min(0)]
      ],
      currency: [data?.currency || 'EUR', Validators.required],
      period: [data?.period || null, Validators.required]
    });
    this.spendRows.push(row);
  }

  onPeriodChange(row: AbstractControl, range: DateRange) {
    const group = row as FormGroup;
    group.get('period')?.setValue(range);
    this.onChange(); // Trigger save
  }

  getTransportModeOptions(
    groupUuid: string | null,
    showRefrigerated: boolean = false
  ): { value: string; label: string }[] {
    if (!groupUuid) return [];

    const modeGroup = this.transportModes.find(m => m.uuid === groupUuid);
    if (!modeGroup?.subModes) return [];

    return modeGroup.subModes.flatMap((mode: any) => {
      const isRefrigerated =
        showRefrigerated &&
        mode.hasRefrigeratedOption &&
        !!mode.refrigeratedUuid;

      if (isRefrigerated && mode.refrigeratedUuid) {
        const labelBase =
          this.language === 'english' ? mode.name : mode.italianName;
        return [
          {
            value: mode.refrigeratedUuid,
            label: `${labelBase} (Refrigerated)`
          }
        ];
      }

      const labelBase =
        this.language === 'english' ? mode.name : mode.italianName;

      return [
        {
          value: mode.uuid,
          label: labelBase
        }
      ];
    });
  }

  addShipment(data: any = null) {
    const shipment = this.fb.group({
      reference: [data?.reference || ''],
      date: [data?.date || null, Validators.required],
      transportModeGroup: [data?.transportModeGroup ?? '', Validators.required],
      transportMode: [data?.transportMode ?? '', Validators.required],
      showRefrigerated: [data?.showRefrigerated || false],
      isRefrigerated: [data?.isRefrigerated || false],
      temperatureControlled: [data?.temperatureControlled || false],
      weight: [data?.weight || null, [Validators.required, Validators.min(0)]],
      distance: [
        data?.distance || null,
        [Validators.required, Validators.min(0)]
      ],
      volume: [data?.volume || null],
      teu: [data?.teu || null],
      commodity: [data?.commodity || '', Validators.required],
      otherCommodity: [data?.otherCommodity || '']
    });

    const options = this.getTransportModeOptions(
      shipment.get('transportModeGroup')?.value,
      shipment.get('showRefrigerated')?.value
    );

    if (data && data.transportMode !== '') {
      const matchedOption = options.find(
        (opt: any) =>
          opt.value === data.transportMode &&
          opt.value === data.transportMode
      );

      shipment.controls['transportMode'].setValue(matchedOption?.value, {
        emitEvent: false
      });
    }

    this.activityShipments.push(shipment);
  }

  removeShipment(index: number) {
    this.activityShipments.removeAt(index);
    this.onChange();
  }

  onModeGroupChange(shipment: AbstractControl) {
    const group = shipment as FormGroup;
    group.get('transportMode')?.setValue('');
    group.patchValue({
      volume: null,
      teu: null,
      isRefrigerated: false
    });
    this.onChange();
  }

  onModeChange(shipment: AbstractControl) {
    const group = shipment as FormGroup;
    group.patchValue({
      volume: null,
      teu: null,
      isRefrigerated: false
    });
    this.onChange();
  }

  onCommodityChange(shipment: AbstractControl) {
    const group = shipment as FormGroup;
    if (group.value.commodity !== '25') {
      group.get('otherCommodity')?.setValue('');
    }
    this.onChange();
  }

  onRefrigeratedToggleChange(shipment: AbstractControl) {
    const group = shipment as FormGroup;
    const currentMode = group.value.transportMode;
    const showRefrigerated = group.value.showRefrigerated;
    const transportModeGroup = group.value.transportModeGroup;

    if (transportModeGroup !== 'road-group') return;

    if (showRefrigerated && currentMode) {
      for (const modeGroup of this.transportModes) {
        const mode = modeGroup.subModes?.find(m => m.uuid === currentMode);
        if (mode?.hasRefrigeratedOption && mode.refrigeratedUuid) {
          group.get('transportMode')?.setValue(mode.refrigeratedUuid);
          group.get('isRefrigerated')?.setValue(true);
          this.onChange();
          return;
        }
      }
    } else if (!showRefrigerated && currentMode) {
      for (const modeGroup of this.transportModes) {
        const mode = modeGroup.subModes?.find(
          m => m.refrigeratedUuid === currentMode
        );
        if (mode) {
          group.get('transportMode')?.setValue(mode.uuid);
          group.get('isRefrigerated')?.setValue(false);
          this.onChange();
          return;
        }
      }
    }

    group.get('transportMode')?.setValue(null);
    this.onChange();
  }

  showRefrigeratedToggle(shipment: AbstractControl): boolean {
    const group = shipment as FormGroup;
    if (group.value.showRefrigerated) return false;
    const modeUuid = group.value.transportMode;
    const transportModeGroup = group.value.transportModeGroup;
    if (transportModeGroup !== 'road-group') return false;
    if (!modeUuid) return false;

    for (const modeGroup of this.transportModes) {
      const mode = modeGroup.subModes?.find(m => m.uuid === modeUuid);
      if (mode?.hasRefrigeratedOption) return true;
    }
    return false;
  }

  showRefrigeratedOptionsToggle(shipment: AbstractControl): boolean {
    const group = shipment as FormGroup;
    return group.value.transportModeGroup === 'road-group';
  }

  showVolumeField(shipment: AbstractControl): boolean {
    const group = shipment as FormGroup;
    const modeUuid = group.value.transportMode;

    if (!modeUuid) return false;

    if (modeUuid?.name === 'Container ship (dry, LCL)') return true;

    return false;
  }

  showTeuField(shipment: AbstractControl): boolean {
    const group = shipment as FormGroup;
    const modeUuid = group.value.transportMode;

    if (!modeUuid) return false;

    if (modeUuid?.name === 'Container ship (dry, FCL)') return true;

    return false;
  }

  trackByOption(index: number, option: any): string {
    return option.value;
  }

  showOtherCommodityInput(shipment: AbstractControl): boolean {
    const group = shipment as FormGroup;
    return group.value.commodity === '25';
  }

  getSavedData(): any {
    const categories = localStorage.getItem('categories');
    if (categories) {
      const parsed = JSON.parse(categories);
      return parsed?.[2]?.questions?.[1]?.answers || null;
    }
    return null;
  }

  // In the onChange method:
  onChange = () => {
    if (this.categories && this.categories.length > 2) {
      const formValue = {
        ...this.form.value,
        spendData: this.form.value.spendData?.map((item: any) => {
          return {
            ...item
          };
        }),
        activityData: this.form.value.activityData?.map((shipment: any) => {
          // Find the selected commodity option
          const selectedCommodity = this.commodityOptions[this.language].find(
            (opt: any) => opt.value === shipment.commodity
          );

          // Get the other language label
          const otherLanguage =
            this.language === 'english' ? 'italian' : 'english';
          const otherLabel = selectedCommodity
            ? selectedCommodity[`${otherLanguage}Label`] ||
              selectedCommodity.label
            : '';

          return {
            ...shipment,
            commodityLabel: selectedCommodity ? selectedCommodity.label : '',
            commodityItalianLabel:
              this.language === 'english'
                ? otherLabel
                : selectedCommodity
                ? selectedCommodity.label
                : '',
            commodityEnglishLabel:
              this.language === 'italian'
                ? otherLabel
                : selectedCommodity
                ? selectedCommodity.label
                : ''
          };
        })
      };

      this.categories[2].questions[1].answers = formValue as any;
      this.categoryService.persistCategories(this.categories);
    }
  };
}
